/**
 * useLocalizedPublicationMetadata Hook
 * 
 * Batch-fetches localized publication metadata (title, excerpt, slug) from localization_metadata table.
 * This follows the metadata-first pattern - no runtime translation.
 */

import { useQuery } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { usePrimaryLocale } from './usePrimaryLocale';
import { useLocale } from '@/contexts/LocaleContext';

interface LocalizedPublicationData {
  title?: string;
  excerpt?: string;
  slug?: string;
}

interface LocalizedPublicationMetadataResult {
  getMetadata: (publicationId: string) => LocalizedPublicationData | undefined;
  metadataMap: Map<string, LocalizedPublicationData>;
  isLoading: boolean;
  needsLocalization: boolean;
}

export function useLocalizedPublicationMetadata(publicationIds: string[]): LocalizedPublicationMetadataResult {
  const { settings } = useLocale();
  const { resolveToPrimary } = usePrimaryLocale();
  
  const contentLocale = resolveToPrimary(settings.locale);
  const needsLocalization = contentLocale !== 'en';
  
  const { data: metadataMap = new Map(), isLoading } = useQuery({
    queryKey: ['localized-publication-metadata', publicationIds, contentLocale],
    queryFn: async () => {
      if (!publicationIds.length) return new Map<string, LocalizedPublicationData>();
      
      const metadataData = await db.fetchAll<{
        source_id: string;
        translated_name: string | null;
        translated_body_short: string | null;
        translated_slug: string | null;
      }>(
        'localization_metadata',
        'source_id, translated_name, translated_body_short, translated_slug',
        {
          filters: [
            { column: 'source_type', operator: 'eq', value: 'publication' },
            { column: 'locale', operator: 'eq', value: contentLocale },
            { column: 'source_id', operator: 'in', value: publicationIds }
          ]
        }
      );
      
      const map = new Map<string, LocalizedPublicationData>();
      metadataData?.forEach(m => {
        map.set(m.source_id, {
          title: m.translated_name ?? undefined,
          excerpt: m.translated_body_short ?? undefined,
          slug: m.translated_slug ?? undefined,
        });
      });
      
      return map;
    },
    enabled: publicationIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  
  const getMetadata = (publicationId: string): LocalizedPublicationData | undefined => {
    return metadataMap.get(publicationId);
  };
  
  return {
    getMetadata,
    metadataMap,
    isLoading,
    needsLocalization
  };
}
